/**
 * MapDialog (#805)
 *
 * One dialog for the map surfaces, configurable along three INDEPENDENT axes:
 *
 *   map-aware  — anchored to a feature's lng/lat, re-projected as the map moves,
 *                so it stays attached to what it describes. Otherwise it is
 *                positioned in the viewport.
 *   modal      — blocks interaction behind it. OFF by default: a map dialog that
 *                blocks the map is the bug #805 was raised for.
 *   moveable   — draggable by its header, constrained to the map area.
 *
 * Any combination is valid and each surface chooses its own; the AFF aircraft
 * click uses map-aware + non-modal + moveable.
 *
 * This sits alongside AppModal rather than inside it. AppModal portals to
 * document.body, always paints a full-screen overlay and is always centred —
 * each of the three axes here NEGATES one of those, and 27 files depend on that
 * behaviour. Dragging reuses useWindowDrag rather than adding a third drag
 * implementation to the two that already exist.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useWindowDrag } from '../../components/explorers/baseExplorer/useWindowDrag';
import { MAP_TOP_OFFSET } from '../../components/explorers/baseExplorer/clampWindowToViewport';
import './MapDialog.scss';

/** Only what the dialog needs of a map — keeps this testable without mapbox. */
export interface MapDialogMap {
  project: (lngLat: { lng: number; lat: number }) => { x: number; y: number };
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
  /**
   * project() returns coordinates relative to the MAP CONTAINER, which sits
   * below the top bar. The dialog is position:fixed, so those numbers have to
   * be shifted into viewport space or everything anchored renders too high by
   * the container's offset and the marker ends up underneath the panel.
   */
  getContainer?: () => { getBoundingClientRect: () => { top: number; left: number } };
}

export interface MapDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  header?: React.ReactNode;
  width?: number;
  showClose?: boolean;

  /** Axis 1 — anchor to a feature. Requires `map`. */
  anchor?: { lng: number; lat: number };
  map?: MapDialogMap | null;

  /** Axis 2 — block interaction behind the dialog. Off by default. */
  modal?: boolean;

  /** Axis 3 — draggable by its header. */
  moveable?: boolean;

  /**
   * Height in px of the icon at the anchor, so the dialog clears its bottom
   * edge by a constant gap whatever size that icon is. Omit for an anchor with
   * no icon of its own.
   */
  anchorIconHeight?: number;

  /** Accessible name for the dialog (#866). The header is free-form markup. */
  ariaLabel?: string;
}

const DEFAULT_WIDTH = 320;
/**
 * Gap between the anchor icon's bottom edge and the dialog.
 *
 * Markers are centred on the projected point, so measuring from the point alone
 * makes the gap shrink as the icon grows — AFF icons range 30-50px, and the
 * spacing visibly changed with aircraft type. The caller passes its icon height
 * and the clearance becomes that icon's own bottom edge plus this one constant,
 * so the callout sits the same distance under every marker.
 */
const ANCHOR_GAP = 14;

export const MapDialog: React.FC<MapDialogProps> = ({
  isOpen,
  onClose,
  children,
  header,
  width = DEFAULT_WIDTH,
  showClose = true,
  anchor,
  map,
  modal = false,
  moveable = false,
  anchorIconHeight = 0,
  ariaLabel = 'Dialog',
}) => {
  const isMapAware = !!(anchor && map);
  /** Icons are centred on the point, so only half of one sits below it. */
  const anchorOffsetY = anchorIconHeight / 2 + ANCHOR_GAP;

  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // A drag wins over the anchor for placement, but the dialog keeps following
  // the map by carrying the user's offset — dragging must not detach it.
  const dragOffset = useRef<{ x: number; y: number } | null>(null);
  const [moved, setMoved] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number }>({ width, height: 0 });
  const dialogRef = useRef<HTMLDivElement | null>(null);

  /** Projected (container-relative) point, in viewport coordinates. */
  const anchorPoint = useCallback(() => {
    if (!map || !anchor) return null;
    const p = map.project({ lng: anchor.lng, lat: anchor.lat });
    const rect = map.getContainer?.().getBoundingClientRect();
    return {
      x: p.x + (rect?.left ?? 0),
      y: p.y + (rect?.top ?? MAP_TOP_OFFSET),
    };
  }, [map, anchor?.lng, anchor?.lat]);

  const place = useCallback(() => {
    if (!isMapAware) return;
    const point = anchorPoint();
    if (!point) return;
    const offset = dragOffset.current ?? { x: 0, y: 0 };
    setPosition({
      x: point.x + offset.x,
      y: Math.max(MAP_TOP_OFFSET, point.y + anchorOffsetY + offset.y),
    });
  }, [isMapAware, anchorPoint, anchorOffsetY]);

  // A drag offset belongs to the feature it was made against. Reopening, or
  // re-anchoring to another feature (#866), starts from the anchor again —
  // carrying the old offset put the new dialog wherever the last one was
  // dragged. Declared before the follow effect so placement sees the reset.
  useEffect(() => {
    dragOffset.current = null;
    setMoved(false);
  }, [isOpen, anchor?.lng, anchor?.lat]);

  // Follow the map only while open and only when anchored. The listener is
  // removed on close so a dismissed dialog cannot keep re-projecting.
  useEffect(() => {
    if (!isOpen || !isMapAware || !map) return;

    place();
    map.on('move', place);
    return () => map.off('move', place);
  }, [isOpen, isMapAware, map, place]);

  // #866 — keyboard users land in the dialog when it opens and go back to
  // where they were when it closes (or unmounts, which is how the AFF host
  // closes it).
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      if (previous && previous !== document.body && document.contains(previous)) previous.focus();
    };
  }, [isOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;
    const rect = dialogRef.current.getBoundingClientRect();
    setSize({ width: rect.width || width, height: rect.height || 0 });
  }, [isOpen, width, children]);

  const applyPosition = useCallback((next: { x: number; y: number }) => {
    if (isMapAware) {
      const point = anchorPoint();
      if (point) {
        dragOffset.current = { x: next.x - point.x, y: next.y - (point.y + anchorOffsetY) };
        setMoved(true);
      }
    }
    setPosition(next);
  }, [isMapAware, anchorPoint, anchorOffsetY]);

  const { headerRef, handleMouseDown, handleTouchStart } = useWindowDrag({
    enabled: moveable && isOpen,
    position,
    size,
    setPosition: applyPosition,
  });

  const style = useMemo<React.CSSProperties>(() => (
    isMapAware
      ? { position: 'fixed', left: position.x, top: position.y, width, transform: 'translateX(-50%)' }
      // Centred in the area below the app header, not the whole viewport: a
      // dialog at its max-height would otherwise start under the header (D4).
      : { position: 'fixed', left: position.x || '50%', top: position.y || `calc(50% + ${MAP_TOP_OFFSET / 2}px)`, width,
          transform: position.x ? undefined : 'translate(-50%, -50%)' }
  ), [isMapAware, position, width]);

  if (!isOpen) return null;

  const dialog = (
    <>
      {modal && <div className="map-dialog__overlay" onClick={onClose} />}
      <div
        className="map-dialog"
        style={style}
        ref={dialogRef}
        role="dialog"
        aria-modal={modal}
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {/* Callout back to the feature. Only while the dialog still sits where
            the anchor put it: once dragged it is no longer above the marker, so
            an arrow out of its top edge would point at nothing. */}
        {isMapAware && !moved && <div className="map-dialog__arrow" aria-hidden="true" />}
        {(header || showClose) && (
          <div
            className={`map-dialog__header${moveable ? ' map-dialog__header--draggable' : ''}`}
            ref={moveable ? headerRef : undefined}
            onMouseDown={moveable ? handleMouseDown : undefined}
            onTouchStart={moveable ? handleTouchStart : undefined}
          >
            <div className="map-dialog__title">{header}</div>
            {showClose && (
              <button type="button" className="map-dialog__close" onClick={onClose} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
        )}
        <div className="map-dialog__body">{children}</div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
};

export default MapDialog;
